import { calcBauteil, type Bauteil } from '@wertermittlung/shared';
import { el, fmt, numFormat } from '../../lib/dom.js';
import { fotoRow } from './fotos.js';
import { fieldNum, fieldSelect, fieldTextarea } from './widgets.js';
import type { WizardCtx } from './types.js';

const TYPEN: Bauteil['typ'][] = ['Überdachter Freisitz', 'Anbau', 'Nebengebäude', 'Gartenlaube'];

export function renderBauteile(ctx: WizardCtx, container: HTMLElement): void {
  const head = el('div', { class: 'step-head' });
  head.append(
    el('div', { class: 'eyebrow' }, 'Schritt 2 von 7'),
    el('h2', {}, 'Gartenlaube & Baulichkeiten'),
    el(
      'p',
      {},
      'Zeitwert = Richtwert × Fläche × Anpassungsfaktor − Abschreibung nach Alter und Zustand. Die App rechnet live mit.',
    ),
  );
  container.append(head);

  const liste = el('div');
  container.append(liste);

  if (ctx.p.data.bauteile.length === 0) {
    liste.append(
      el(
        'div',
        { class: 'list-empty' },
        'Keine Baulichkeiten erfasst – falls vorhanden, unten hinzufügen.',
      ),
    );
  }
  ctx.p.data.bauteile.forEach((b, i) => liste.append(bauteilKarte(ctx, b, i)));

  const addRow = el('div', { style: 'display:grid;gap:10px;grid-template-columns:1fr 1fr' });
  for (const typ of TYPEN) {
    const btn = el(
      'button',
      { class: 'btn btn-add', type: 'button' },
      `+ ${typ === 'Gartenlaube' ? 'weitere Laube' : typ}`,
    );
    btn.addEventListener('click', () => {
      ctx.p.data.bauteile.push({
        id: crypto.randomUUID(),
        typ,
        bk: typ === 'Gartenlaube' ? 3 : 2,
        rw: typ === 'Überdachter Freisitz' ? 110 : 340,
        flaeche: null,
        baujahr: null,
        zustand: 1,
        ausfuehrung: '',
        fotos: [],
      });
      ctx.save();
      ctx.recalc();
      ctx.rerender();
    });
    addRow.append(btn);
  }
  container.append(addRow);
}

function bauteilKarte(ctx: WizardCtx, b: Bauteil, index: number): HTMLElement {
  const card = el('div', { class: 'poscard' });
  const head = el('div', { class: 'poshead' });
  head.append(el('strong', {}, b.typ));
  const remove = el(
    'button',
    { class: 'btn-x', type: 'button', 'aria-label': `${b.typ} entfernen` },
    '✕ entfernen',
  );
  remove.addEventListener('click', () => {
    ctx.p.data.bauteile.splice(index, 1);
    ctx.save();
    ctx.recalc();
    ctx.rerender();
  });
  head.append(remove);
  card.append(head);

  const result = el('div', { class: 'posresult' });
  const path = el('span', { class: 'calcpath' });
  const wert = el('strong');
  result.append(path, wert);
  const update = () => {
    const e = calcBauteil(b, ctx.p.faktor, ctx.p.jahr);
    path.textContent = `${numFormat(b.rw)} € × ${numFormat(b.flaeche ?? 0)} m² × ${numFormat(ctx.p.faktor)} − ${numFormat(e.abschreibung)} % (${e.alter} J. × ${numFormat(e.satz)} %)`;
    wert.textContent = fmt(e.zeitwert);
  };

  const onWert = () => {
    ctx.save();
    ctx.recalc();
    update();
  };

  const grid = el('div', { class: 'grid' });
  grid.append(
    fieldSelect(
      'Bauklasse (bestimmt den Richtwert)',
      ctx.katalog.bauklassen.map((k) => ({ value: String(k.k), label: k.label })),
      () => String(b.bk),
      (v) => {
        b.bk = Number(v);
        const bk = ctx.katalog.bauklassen.find((k) => k.k === b.bk);
        if (bk && b.typ !== 'Überdachter Freisitz') b.rw = bk.rw;
      },
      () => {
        onWert();
        ctx.rerender();
      },
      { wide: true },
    ),
    fieldNum(
      'Richtwert je m²',
      () => b.rw,
      (v) => (b.rw = v ?? 0),
      onWert,
      {
        unit: '€/m²',
        hint: 'Vorschlag aus dem Katalog – anpassbar.',
      },
    ),
    fieldNum(
      'Grundfläche',
      () => b.flaeche,
      (v) => (b.flaeche = v),
      onWert,
      { unit: 'm²' },
    ),
    fieldNum(
      'Baujahr',
      () => b.baujahr,
      (v) => (b.baujahr = v),
      onWert,
      {
        placeholder: 'z. B. 1999',
      },
    ),
    fieldSelect(
      'Zustand (Abschreibungssatz je Jahr)',
      ctx.katalog.zustand.map((z, i) => ({
        value: String(i),
        label: `${z.label} · ${numFormat(z.satz)} %`,
      })),
      () => String(b.zustand),
      (v) => (b.zustand = Number(v)),
      onWert,
    ),
    fieldTextarea(
      'Ausführung / Besonderheiten',
      () => b.ausfuehrung,
      (v) => (b.ausfuehrung = v),
      ctx.save,
      { placeholder: 'z. B. Blockbauweise, Punktfundament, Satteldach mit Blech, 2 Fenster' },
    ),
  );
  card.append(grid, fotoRow(ctx, b.fotos), result);
  update();
  return card;
}
